import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Data2Service } from '../../common/data2.service';
import { LoaderService } from '../loader/loader.service';

@Component({
	selector: 'app-dynamic-table',
	templateUrl: './dynamic-table.component.html',
	styleUrls: ['./dynamic-table.component.css'],
})
export class DynamicTableComponent implements OnChanges {
	tableShowOptions = false;
	allElements: Array<object> = [];
	sumElements: Array<object> = [];
	private filtredElements: Array<object> = [];
	showElements: Array<object> = [];
	showColumns: Array<object> = [];
	filterValue = '';
	viewIsSet = false;
	private scrollToId = '';
	pages = {
		count: 0,
		pages: [],
		offset: {
			start: null,
			end: null,
		},
	};
	settings = {
		sort: {
			key: '',
			ascending: false,
		},
		hiddenColumns: [],
		currentPage: 1,
	};

	@Input() module = '';
	@Input() tableRows: Array<any> = [];
	@Input() elements: Array<any> = [];
	@Input() haveLink = true;
	@Input() haveButton = false;
	@Input() buttonText = '&gt;&gt;';
	@Input() linkAccess = true;
	@Input() filtersSet = true;
	@Input() haveFilters = true;
	@Input() haveAnyFilters = true;
	@Input() elementLinkPostfix = '';
	@Input() rowsPerPage = 50;
	@Input() showHeader = true;
	@Output() filtersClicked = new EventEmitter();
	@Output() buttonClicked = new EventEmitter();
	@Output() filtersChanged = new EventEmitter();

	constructor(
		private readonly api: Data2Service,
		private readonly loader: LoaderService,
		private readonly route: ActivatedRoute,
	) {
		this.route.fragment.subscribe((fragment: string) => {
			this.scrollToId = fragment;
		});
	}

	ngOnChanges(): void {
		if (!this.elements) {
			return;
		}
		this.settingsLoad();
		this.loadData();
		this.generatePagination();
		this.pageChange(this.settings.currentPage, false);
		this.scrollToLastVisited();
	}

	private loadData(): void {
		this.loader.show();
		this.allElements = this.elements.map((obj) => {
			const row = {
				class: null,
				style: null,
				link: null,
				tableElementId: null,
				id: null,
			};
			this.tableRows.forEach((column) => {
				if (this.settings.hiddenColumns.includes(column.id)) {
					// hide this column
					return;
				}
				row[column.id] = obj[column.id];
			});
			row.class = obj?.class;
			// mark row as last viewed
			if (this.scrollToId && this.scrollToId == obj.id) {
				row.class = 'lastEdited';
			}
			row.style = obj?.style;
			row.link = '/' + this.module + '/' + obj.id + '/' + this.elementLinkPostfix;
			row.tableElementId = 'tableElement-' + obj.id;
			row.id = obj.id;
			return row;
		});
		if (!this.allElements.length) {
			this.showElements = [];
			this.allElements = [];
			this.filtredElements = [];
			this.loader.hide();
			return;
		}
		this.generateColumns();
		this.generateFiltred();
	}

	private generateColumns(): void {
		this.showColumns = this.tableRows.filter((obj) => !this.settings.hiddenColumns.includes(obj.id));
	}

	private generateFiltred() {
		const searchValue = this.filterValue.trim().toLowerCase();
		if (searchValue.length >= 3) {
			this.filtredElements = this.allElements.filter((obj) => {
				return Object.keys(obj).some((key) => {
					if (obj[key] == null) {
						return;
					}
					return obj[key].toString().toLowerCase().includes(searchValue);
				});
			});
		} else {
			this.filtredElements = this.allElements;
		}
		this.sortData();
		this.generatePagination();
		this.pageChange(this.settings.currentPage, false);
		this.filtersChanged.emit(this.filtredElements);
	}

	private generatePagination(): void {
		this.pages.count = Math.ceil(this.filtredElements.length / this.rowsPerPage);
		this.pages.pages = Array(this.pages.count)
			.fill(0)
			.map((e, i) => i + 1);
	}

	private generateElements(): void {
		this.showElements = this.filtredElements.slice(this.pages.offset.start, this.pages.offset.end);
	}

	pageChange(pageNo: number, save = true): void {
		if (!this.pages.count) {
			return;
		}
		this.loader.show();
		if (pageNo > this.pages.count) {
			pageNo = 1;
		}
		this.settings.currentPage = pageNo;
		this.pages.offset = {
			start: (this.settings.currentPage - 1) * this.rowsPerPage,
			end: this.settings.currentPage * this.rowsPerPage,
		};
		this.generateElements();
		this.loader.hide();
		if (save) {
			this.settingsSave();
		}
	}

	filersOpen(): void {
		this.filtersClicked.emit(true);
	}

	onButtonClick(element): void {
		this.buttonClicked.emit(element);
	}

	showHideRow(itemId): void {
		if (!this.settings.hiddenColumns.includes(itemId)) {
			this.settings.hiddenColumns.push(itemId);
		} else {
			this.settings.hiddenColumns.splice(this.settings.hiddenColumns.indexOf(itemId), 1);
		}
		this.loadData();
		this.settingsSave();
		this.viewIsSet = this.settings.hiddenColumns && this.settings.hiddenColumns.length ? true : false;
		this.loader.hide();
	}

	refreshFilter() {
		const searchValue = this.filterValue.trim().toLowerCase();
		if (searchValue.length < 3 && searchValue.length > 0) {
			return;
		}
		this.generateFiltred();
	}

	scrollToLastVisited() {
		if (!this.scrollToId) {
			return;
		}
		setTimeout(() => {
			const lastEditedElement = document.getElementById('tableElement-' + this.scrollToId);
			if (!lastEditedElement) {
				return;
			}
			window.scrollTo({
				top: lastEditedElement.getBoundingClientRect().top - 70,
				behavior: 'smooth',
			});
		});
	}

	setSort(key): void {
		this.loader.show();
		this.settings.sort.key = key;
		this.settings.sort.ascending = !this.settings.sort.ascending;
		this.settingsSave();
		this.sortData();
		this.generateElements();
		this.loader.hide();
	}

	private sortData() {
		const { key, ascending } = this.settings.sort;
		this.filtredElements.sort((a, b) => {
			const valueA = a[key];
			const valueB = b[key];
			return (ascending ? 1 : -1) * (valueA > valueB ? 1 : valueA < valueB ? -1 : 0);
		});
	}

	private settingsSave(): void {
		localStorage.setItem('pageSettings-' + this.module, JSON.stringify(this.settings));
	}

	private settingsLoad(): void {
		if (!localStorage['pageSettings-' + this.module]) {
			return;
		}
		this.settings = JSON.parse(localStorage['pageSettings-' + this.module]);
		this.viewIsSet = this.settings.hiddenColumns && this.settings.hiddenColumns.length ? true : false;
	}
}
